import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import { LeftArrowIcon } from "../../../../assets/icons/svgicons";
import Button from "../../Button/Button";
import InputFormik from "../../Formik/Input/InputFormik";
import Select from "../../Formik/Select/Select";
import Modal from "../Modal/Modal";
import styles from "./AddAddress.module.scss";
import Checkbox from "../../Formik/Checkbox/Checkbox";
import csc from "country-state-city";
import SearchLocationInput from "./GooglePlacesApi";
import MapComponent from "./Map";
import { useLoadScript } from "@react-google-maps/api";
import { GOOGLE_PLACES_API_KEY } from "../../../../utils/constants";
import * as Yup from "yup";
import stateConverter from "us-state-converter";

import { getCode } from "country-list";

const getShortName = (stateFullName, countryFullName) => {
  const stateAbbreviation = stateConverter.abbr(stateFullName);
  const countryAbbreviation = getCode(countryFullName);
  return {
    stateAbbreviation,
    countryAbbreviation,
  };
};

const AddAddress = ({
  show,
  handleClose,
  address,
  setAddress,
  saveAddress,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_PLACES_API_KEY,
    libraries: ["places"], // Important to include places library
  });

  const [selectedLocation, setSelectedLocation] = useState({
    lat: 28.7041,
    lng: 77.1025,
  });
  const [query, setQuery] = useState(
    selectedLocation.displayAddress ? selectedLocation.displayAddress : ""
  );
  const [addressDetail, setAddressDetail] = useState({});
  const [sameAddress, setSameAddress] = useState(false);
  const [isManual, setIsManual] = useState(false);

  const countries = csc.getAllCountries();
  const updatedCountries = countries.map((country) => ({
    label: country.name,
    value: country.id,
    ...country,
  }));

  const updatedStates = (countryId) =>
    csc
      .getStatesOfCountry(countryId)
      .map((state) => ({ label: state.name, value: state.id, ...state }));

  const shipValidationSchema = {
    shippingaddress: Yup.string()
      .label("Shipping Address")
      .required("Please enter your shipping address"),
    city: Yup.string().label("City").required("Please enter your city"),
    zipcode: Yup.string()
      .label("Zip Code")
      .required("Please enter your area zip code"),
    state: Yup.object(),
    country: Yup.object().required("Please select your country"),
  };

  const billingValidationSchema = {
    billingAddress: Yup.string()
      .label("Billing Address")
      .required("Please enter your billing address"),
    billingCity: Yup.string()
      .label("Billing City")
      .required("Please enter your billing city"),
    billingZipcode: Yup.string()
      .label("Billing Zip Code")
      .required("Please enter your billing zip code"),
    billingState: Yup.object(),
    billingCountry: Yup.object().required("Please select your billing country"),
  };

  let validationSchema;
  if (sameAddress) {
    validationSchema = Yup.object({
      ...shipValidationSchema,
      ...billingValidationSchema,
    });
  } else {
    validationSchema = Yup.object(shipValidationSchema);
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      shippingaddress: "",
      city: "",
      zipcode: "",
      state: "",
      country:
        updatedCountries?.find((country) => country.name === "United States") ||
        null,
      address_different: false,
      billingAddress: "",
      billingCity: "",
      billingZipcode: "",
      billingState: null,
      billingCountry: null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const shippingState = values.state?.label || "";
      const shippingCountry = values.country?.label || "";
      const billingState = values.billingState?.label || "";
      const billingCountry = values.billingCountry?.label || "";

      const shippingAbbreviations = getShortName(
        shippingState,
        shippingCountry
      );
      const billingAbbreviations = getShortName(billingState, billingCountry);

      const updatedValues = {
        ...values,
        shippingState: shippingAbbreviations?.stateAbbreviation || "",
        shippingCountry: shippingAbbreviations?.countryAbbreviation || "",
        billingState: billingAbbreviations?.stateAbbreviation || "",
        billingCountry: billingAbbreviations?.countryAbbreviation || "",
      };

      formik.setSubmitting(false);
      if (formik.isValid) {
        saveAddress(updatedValues);
        handleClose();
      }
    },
  });

  // useEffect(() => {
  //   formik.setValues({
  //     ...formik.values,
  //     shippingaddress: addressDetail.address,
  //     city: addressDetail.city,
  //     zipcode: addressDetail.zipCode,
  //     state: { label: addressDetail.state, name: addressDetail.state },
  //     country: {
  //       label: addressDetail.country || "United States",
  //       name: addressDetail.country || "United States",
  //     },
  //   });
  // }, [addressDetail]);

  useEffect(() => {
    if (formik.values.country && !formik.values.state) {
      const states = updatedStates(formik.values.country.value);
      formik.setFieldValue("state", states.length > 0 ? { ...states[0], label: states[0].name, value: states[0].id } : null);
    }
  }, [formik.values.country]);

  useEffect(() => {
    if (addressDetail && addressDetail.streetAddress) {
      const countryName = addressDetail.country || "United States";
      const country = updatedCountries?.find((c) => c.name === countryName);
      const states = updatedStates(country ? country.id : null);

      const state = states?.find((s) => s.name === addressDetail.state);

      formik.setValues({
        ...formik.values,
        shippingaddress: addressDetail.streetAddress,
        city: addressDetail.city,
        zipcode: addressDetail.zipCode,
        state: state ? { ...state, label: state.name, value: state.id } : null,
        country: country ? { ...country, label: country.name, value: country.id } : null,
      });
    }
  }, [addressDetail]);

  useEffect(() => {
    if (loadError) {
      setIsManual(true);
    }
  }, [loadError]);

  const handleAddressChange = (newAddress) => {
    setAddress(newAddress);
    setQuery(newAddress);
  };

  const addAddress = () => {
    if (
      formik?.values?.city &&
      formik?.values?.state?.value &&
      formik?.values?.country?.value &&
      formik?.values?.zipcode &&
      formik?.values?.shippingaddress
    ) {
      saveAddress(formik?.values);
      handleClose();
    }
  };

  const containerStyle = {
    height: "200px",
  };

  return (
    <>
      <Modal
        show={show}
        handleClose={handleClose}
        title={
          <span onClick={handleClose}>
            <LeftArrowIcon />
            Back
          </span>
        }
        className={styles.add_address}
        hideCloseBtn={true}
      >
        {!isManual && (
          <MapComponent
            selectedLocation={selectedLocation}
            formik={formik}
            setAddressDetail={setAddressDetail}
            setSelectedLocation={setSelectedLocation}
            containerStyle={{ width: "100%", height: "400px" }}
            onAddressChange={handleAddressChange}
            query={query}
            setQuery={setQuery}
            countries={updatedCountries}
            isLoaded={isLoaded}
            loadError={loadError}
          />
        )}
        <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          <Row>
            <Col sm={12}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="mb-0" style={{ fontWeight: '500' }}>
                  {isManual ? "Shipping Address" : "Search Shipping Address"}
                </label>
                {!loadError && (
                  <span
                    style={{ color: '#007bff', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                    onClick={() => setIsManual(!isManual)}
                  >
                    {isManual ? "Use Map Search" : "Enter Manually"}
                  </span>
                )}
              </div>
              {isManual ? (
                <InputFormik
                  name="shippingaddress"
                  value={formik?.values?.shippingaddress}
                  placeholder="Please enter your shipping address"
                  variant=""
                  className={styles.input}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  formik={formik}
                />
              ) : (
                <SearchLocationInput
                  query={query}
                  setQuery={setQuery}
                  setSelectedLocation={setSelectedLocation}
                  setAddressDetail={setAddressDetail}
                  userAddress={{ ...selectedLocation, disableAutocomplete: loadError }}
                  name="shippingaddress"
                  id="shippingaddress"
                  type="shippingaddress"
                  onChange={(e) =>
                    formik.setFieldValue("shippingaddress", e.target.value)
                  }
                  handleBlur={() =>
                    formik.setTouched({
                      ...formik.touched,
                      shippingaddress: true,
                    })
                  }
                  formik={formik}
                  isLoaded={isLoaded}
                />
              )}
            </Col>
            <Col sm={12}>
              <InputFormik
                name="zipcode"
                value={formik?.values?.zipcode}
                label="Zip Code"
                placeholder="Please enter your area zip code"
                variant=""
                className={styles.input}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                formik={formik}
              />
            </Col>
            <Col sm={12}>
              <InputFormik
                name="city"
                label="City"
                placeholder="Please enter your City"
                variant=""
                className={styles.input}
                onChange={formik.handleChange}
                value={formik?.values?.city}
                onBlur={formik.handleBlur}
                formik={formik}
              />
            </Col>
            <Col sm={6}>
              <Select
                name="country"
                label="Country"
                variant=" large_height"
                placeholder={<h6>Select your country</h6>}
                className={styles.select}
                defaultValue={
                  updatedCountries?.find(
                    (country) => country.name === "United States"
                  ) || {}
                }
                value={formik?.values?.country}
                onChange={(value) => {
                  formik.setValues(
                    {
                      ...formik.values,
                      country: value,
                      state: null,
                    },
                    false
                  );
                }}
                options={updatedCountries}
              />
            </Col>
            <Col sm={6}>
              <Select
                name="state"
                label="State"
                placeholder={<h6>Select your state</h6>}
                variant=" large_height"
                options={updatedStates(
                  formik.values.country ? formik.values.country.value : null
                )}
                className={styles.select}
                value={formik?.values?.state}
                onChange={(value) => {
                  formik.setValues(
                    {
                      ...formik.values,
                      state: value,
                    },
                    false
                  );
                }}
              />
            </Col>
            <Col sm={12}>
              <Checkbox
                name="address_different"
                checked={sameAddress}
                onChange={(e) => setSameAddress(e.target.checked)}
                label="Select, if your home or business address is different than your shipping address."
              />
            </Col>
            {sameAddress || formik.values.address_different ? (
              <>
                <Col sm={12}>
                  <InputFormik
                    name="billingAddress"
                    multiline={true}
                    rows={2}
                    id="billingAddress"
                    type="billingAddress"
                    value={formik?.values?.billingAddress}
                    label="Billing Address"
                    placeholder="Please enter your billing address"
                    variant=""
                    className={styles.input}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    formik={formik}
                  />
                </Col>
                <Col sm={12}>
                  <InputFormik
                    name="billingZipcode"
                    id="billingZipcode"
                    type="billingZipcode"
                    value={formik?.values?.billingZipcode}
                    label="Billing Zip Code"
                    placeholder="Please enter your billing zip code"
                    variant=""
                    className={styles.input}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    formik={formik}
                  />
                </Col>
                <Col sm={12}>
                  <InputFormik
                    name="billingCity"
                    id="billingCity"
                    type="billingCity"
                    value={formik?.values?.billingCity}
                    label="Billing City"
                    placeholder="Please enter your billing city"
                    variant=""
                    className={styles.input}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    formik={formik}
                  />
                </Col>
                <Col sm={6}>
                  <Select
                    name="billingCountry"
                    label="Billing Country"
                    variant=" large_height"
                    placeholder={<h6>Select your billing country</h6>}
                    className={styles.select}
                    value={formik?.values?.billingCountry}
                    onChange={(value) =>
                      formik.setValues(
                        {
                          ...formik.values,
                          billingCountry: value,
                          billingState: null,
                        },
                        false
                      )
                    }
                    options={updatedCountries}
                  />
                </Col>
                <Col sm={6}>
                  <Select
                    name="billingState"
                    label="Billing State"
                    placeholder={<h6>Select your billing state</h6>}
                    variant=" large_height"
                    options={updatedStates(
                      formik.values.billingCountry
                        ? formik.values.billingCountry.value
                        : null
                    )}
                    className={styles.select}
                    value={formik?.values?.billingState}
                    onChange={(value) => {
                      formik.setValues(
                        {
                          ...formik.values,
                          billingState: value,
                        },
                        false
                      );
                    }}
                  />
                </Col>
              </>
            ) : null}
          </Row>
          <Row>
            <Col sm={12}>
              <Button
                type="button"
                fluid={true}
                className={styles.add_btn}
                onClick={(e) => {
                  formik.handleSubmit(e);
                }}
              >
                Add Address
              </Button>
            </Col>
          </Row>
        </form>
      </Modal>
    </>
  );
};

export default AddAddress;
